import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import DocumentAdd from 'components/Icons/DocumentAdd';
import UploadSingle from 'components/Icons/UploadSingle';
import PrescriptionUploadForm from 'components/Prescription/PrescriptionUploadForm';
import {
  isMultifocalPrescription,
  isPrescriptionOnlyHasFile
} from 'functions/multifocal';
import { GET_PRESCRIPTIONS, GET_PDS } from 'gqlStrings/queries/getPrescriptions';
import {
  setPds,
  setOpenPrescriptionSelection,
  setPdSelectPrescriptionId,
  setPrescriptions,
  setSelectedPrescription,
  setSelectEditedPrescription
} from 'redux/features/prescription/prescriptionSlice';
import { RootState } from 'redux/store';
import Drawer from '../Drawer/Drawer';
import PrescriptionForm from '../Prescription/PrescriptionForm';
import Spinner from '../Spinner/Spinner';
import PrescriptionBox from './PrescriptionBox';
import UIButton from './UIButton';
import DrawerNavigation from './UIDrawerNavigation';
import { useGlassesStore } from 'zus/glassesStore';

export default function SelectPrescription() {
  const isLogin = useSelector((state: RootState) => state.authEvent.isLogin);

  const prescriptions = useSelector(
    (state: RootState) => state.prescriptionEvent.prescriptions
  );

  const selectedPrescription = useSelector(
    (state: RootState) => state.prescriptionEvent.selectedPrescription
  );

  const selectBoxPrescription = useSelector(
    (state: RootState) => state.prescriptionEvent.selectBoxPrescription
  );

  const openPrescriptionSelection = useSelector(
    (state: RootState) => state.prescriptionEvent.openPrescriptionSelection
  );

  const purpose = useGlassesStore((state) => state.purpose)

  const isMultifocal = (purpose == 'multifocal' || purpose == 'bifocal');

  const [home, setHome] = useState(true);
  const [prescriptionAddForm, setPrescriptionAddForm] = useState(false);
  const [prescriptionUploadForm, setPrescriptionUploadForm] = useState(false);
  const [uploadStep, setUploadStep] = useState<'UPLOAD' | 'FORM'>('UPLOAD')

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!openPrescriptionSelection) return;
    if (isLogin) {
      setLoading(true);
      getPrescriptionsMutation();
      return;
    }

    setPrescriptionAddForm(true);
    setHome(false);
    dispatch(setPdSelectPrescriptionId(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPrescriptionSelection]);


  const [getPrescriptionsMutation] = useLazyQuery(GET_PRESCRIPTIONS, {
    context: { clientName: 'laravel' },
    onCompleted: (getPrescriptions) => {
      if (getPrescriptions.me.prescriptions.data.length > 0) {
        var prescriptions = [...getPrescriptions.me.prescriptions.data];
        prescriptions = prescriptions.filter((prescription: any) => prescription.type_id == 1);
        dispatch(
          setPrescriptions(
            prescriptions.reverse().sort((a) => {
              return selectBoxPrescription?.id === a.id ? -1 : 1;
            })
          )
        );
      }
      getPds();
    }
  });
  const [getPds] = useLazyQuery(GET_PDS, {
    context: { clientName: 'laravel' },
    onCompleted: (getPds) => {
      dispatch(setPds(getPds.me.pds.data));
      setLoading?.(false);
      if (sortedPrescriptions.length || isMultifocal) return;
      setPrescriptionAddForm(true);
      setHome(false);
      dispatch(setPdSelectPrescriptionId(null));
    }
  });

  const submitSelectedPrescription = (payload: any) => {
    dispatch(setSelectedPrescription(payload));
    dispatch(setOpenPrescriptionSelection(false));
  };

  const sortedPrescriptions = useMemo(() => {
    if (!Array.isArray(prescriptions)) return [];

    return [...prescriptions]
      .filter(pPrescription => {
        if (isMultifocal) return isMultifocalPrescription(pPrescription);

        return !isPrescriptionOnlyHasFile(pPrescription);
      })
      .sort((a) => selectedPrescription?.id === a.id ? -1 : 1);
  }, [isMultifocal, prescriptions, selectedPrescription?.id]);



  const navigationTitle = useMemo(() => {
    if (home) return 'Select Prescription';
    if (prescriptionAddForm) return 'Enter Your Prescription'
    if (prescriptionUploadForm && uploadStep === 'UPLOAD') return 'Upload Your Prescription'
    if (prescriptionUploadForm && uploadStep === 'FORM') return 'Enter Your Prescription'

    return 'Your Prescription';
  }, [home, prescriptionAddForm, prescriptionUploadForm, uploadStep]);

  const drawerNavigateBackHandler = () => {
    setPrescriptionUploadForm(false);
    setPrescriptionAddForm(false);
    setHome(true);
  }

  const drawerNavigationCloseHandler = () => {
    document?.body?.classList?.remove('overflow-hidden-body');
    dispatch(setOpenPrescriptionSelection(false));
    dispatch(setPdSelectPrescriptionId(null));
    dispatch(setSelectEditedPrescription(null));
  }


  if (!openPrescriptionSelection) return null;

  return (
    <Drawer
      position="right"
      open={openPrescriptionSelection}
      onCallEndAnimate={() => {
        document?.body?.classList?.remove('overflow-hidden-body');
        dispatch(setOpenPrescriptionSelection(false));
      }}
      className="w-[680px] overflow-y-auto bg-white mdx:w-full"
      clickable
      onClose={() => {
        document?.body?.classList?.remove('overflow-hidden-body');
        dispatch(setOpenPrescriptionSelection(false));
        dispatch(setPdSelectPrescriptionId(null));
        dispatch(setSelectEditedPrescription(null));
      }}>
      <div className="h-full overflow-y-auto">
        {!loading && <div className="w-full px-[24px] py-[12px] mb-[12px]">
          <DrawerNavigation title={navigationTitle} subTitle={purpose!} hideBack={!prescriptionAddForm && !prescriptionUploadForm} onNavigateBack={drawerNavigateBackHandler} onClose={drawerNavigationCloseHandler} />
        </div>}
        <div className="flex flex-col items-center px-9 mdx:px-3">
          {home && (
            <>
              {!loading && <UIButton
                icon={<DocumentAdd fill="currentColor" className="mr-[8px]" />}
                block
                light
                className="mb-[12px]"
                onClick={() => {
                  setPrescriptionAddForm(true);
                  setHome(false);
                  dispatch(setPdSelectPrescriptionId(null));
                }}>
                Create new prescription
              </UIButton>}

              {!loading && isMultifocal && (
                <UIButton
                  icon={<UploadSingle fill="currentColor" className="mr-[8px]" />}
                  block
                  light
                  onClick={() => {
                    setPrescriptionUploadForm(true);
                    setHome(false);
                    dispatch(setPdSelectPrescriptionId(null));
                  }}
                >
                  Upload prescription
                </UIButton>
              )}

              <div className="w-full h-full pb-6">
                {loading ? (
                  <div className="w-full h-full pt-[32px] flex items-center justify-center">
                    <Spinner fill="black" width={32} height={32} />
                  </div>
                ) : null}
                {!loading &&
                  sortedPrescriptions.map((prescription: any) => {
                    if (!prescription.contactLensPrescription) {
                      return (<PrescriptionBox
                        key={prescription.id}
                        prescription={prescription}
                        setPrescriptionEditForm={setPrescriptionAddForm}
                        onSubmit={submitSelectedPrescription}
                        selectDefault={sortedPrescriptions.length === 1}
                        setHome={setHome} />);
                    }
                  }
                  )}
              </div>
            </>
          )}
          {prescriptionAddForm && (
            <PrescriptionForm
              setPrescriptionAddForm={setPrescriptionAddForm}
              setHome={setHome}
            />
          )}

          {prescriptionUploadForm && <PrescriptionUploadForm onVisibleStepChanged={setUploadStep} setPrescriptionUploadForm={setPrescriptionUploadForm} setHome={setHome} />}
        </div>
      </div>
    </Drawer>
  );
}
